import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  MenuItem,
  Typography,
  Tab,
  Tabs,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
} from "@material-ui/core";
import SwipeableViews from "react-swipeable-views";
import { getServerUri } from "../modules/uriUtility";
import { filterTabDefs } from "../modules/editorTabAccess";
import { shouldRebuildPanel } from "../modules/panelFreeze";
import { needsAssetMapMigration, migrateConfigToAssetMap } from "../modules/playEngineAssetMigration";
import { buildPresetDashboardMeta, resolvePresetDashboardMeta } from "../modules/playEnginePresetDashboardMetaUtils";
import {
  ensureLocalizationOverrideLanguage,
  stripLanguageFromLocalizationOverrides
} from "../modules/localizationOverrides";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import SettingsIcon from "@material-ui/icons/Settings";
import LanguageIcon from "@material-ui/icons/Language";
import TuneIcon from "@material-ui/icons/Tune";
import ListAltIcon from "@material-ui/icons/ListAlt";
import PaletteIcon from "@material-ui/icons/Palette";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import CodeIcon from "@material-ui/icons/Code";
import AssistantIcon from "@material-ui/icons/Assistant";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import SaveIcon from "@material-ui/icons/Save";
import PropTypes from "prop-types";
import VersionSettingsContainer from "./containerVersionSettings";
import VersionSettingsContainerTreeView from "./containerVersionSettingsTreeView";
import TextGameObjectEditorContainer from "./containerTextGameObject";
import LocalizationContainer from "./containerLocalization";
import LocalizationContainerTreeView from "./containerLocalizationTreeView";
import GeneralSettingsEditor from "./editorGeneralSettings";
import ContainerGamesAgent from "./containerGamesAgent";
import PlayEngineVersionSettings from "./containerPlayEngineVersionSettings";
import PlayEngineAssets from "./containerPlayEngineAssets";
import PlayEngineGeneralSettings from "./containerPlayEngineGeneralSettings";
import PlayEngineCustomVariables from "./containerPlayEngineCustomVariables";
import PlayEngineDesign from "./playEngineDesign/containerPlayEngineDesign";
import PlayEngineSounds from "./containerPlayEngineSounds";
import PlayEngineScripts from "./containerPlayEngineScripts";
import PlayEngineAgent from "./containerPlayEngineAgent";
import SceneEditor from "./sceneEditor/containerSceneEditor";
import SceneEditorGeneralSettings from "./sceneEditor/containerSceneEditorGeneralSettings";
import { hasOwnSceneEditorCategoryData, stripSceneEditorCategoryLayout } from "../modules/sceneEditorMetadataFromSections";
import ReactGA from "react-ga";
import ReactGA4 from "react-ga4";

import { UndoContext } from "../contexts/UndoContext";
import _ from "lodash";

/**
 * @returns {JSX.Element}
 * @constructor
 */
ReactGA.initialize("UA-186063268-1", {
  debug: false,
  gaOptions: {
    userId: localStorage.getItem("user.id")
  }
});
ReactGA4.initialize("G-TB46JQ70PR", {
  debug: false,
  gaOptions: {
    userId: localStorage.getItem("user.id")
  }
});
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      <Box p={3}>
        <Typography component={"span"}>{children}</Typography>
      </Box>
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`
  };
}

const useStyles = makeStyles((theme) => ({
  gameConfigContainerDiv: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "435px",
    minWidth: "500px"
  },
  tabPanel: {
    "& .MuiBox-root": { padding: "0px" },
    height: "100%",
    overflowY: "auto",

    "&::-webkit-scrollbar": {
      display: "none"
    }
  }
}));

/**
 *
 * @param props.selectedGame {Object}
 * @param props.selectedBase {Object}
 * @param props.selectedModifiedBase {Object}
 * @param props.setSelectedModifiedBase {Object}
 * @param props.selectedBase.supportedLanguages
 * @param props.setSelectedVersionPathValue
 * @param props.selectedVersion {Object}
 * @param props.media
 * @param props.areMediaLoaded
 * @param props.atlases
 * @param props.areAtlasesLoaded
 * @param props.spines
 * @param props.areSpinesLoaded
 * @param props.sounds
 * @param props.areSoundsLoaded
 * @param props.fonts
 * @param props.areFontsLoaded
 * @param props.videos
 * @param props.areVideosLoaded
 * @param props.insertMediaPathValue
 * @param props.insertAtlasPathValue
 * @param props.insertSpinePathValue
 * @param props.insertSoundPathValue
 * @param props.insertFontPathValue
 * @param props.insertVideoPathValue
 * @param props.insertGlbPathValue
 * @param props.setSelectedVersionPathValue {function}
 * @param props.addDefaultImagesToGameConfig {function}
 * @param props.classes
 * @param props.setIsPreviewFullScreen {function}
 * @param props.doRefresh {function}
 * @param props.maxInnerHeight
 * @param props.isRecordingStarted
 * @param props.setIsRecordingStarted {function}
 * @param props.deleteImageFromMedia
 * @param props.deleteImageSetFromMedia
 * @param props.deleteFontFromFonts
 * @param props.deleteSoundFromSounds
 * @param props.deleteVideoFromVideos
 * @param props.deleteAnimationSetFromAtlases
 * @param props.deleteAnimationSetFromSpines
 * @param props.deleteGlbFromGlbs
 * @param props.createSnackbar
 * @param props.userPermissions
 * @param props.setIsDialogConfirmRefreshOpen
 * @param props.setIsDialogCircularProgressOpen
 * @returns {JSX.Element}
 */

export default function containerGameConfig(props) {
  const isPlayEngine = _.get(props, "selectedBase.playEngineInstance", false);
  const isSceneEditor = !isPlayEngine && _.get(props, "selectedBase.sceneEditor", false) === true;
  // Guard: nothing to render without a version yet
  if (_.isNil(props.selectedVersion)) return null;
  // For regular games require gameConfig + loaded media; playEngine instances are exempt
  if (
    !isPlayEngine &&
    (!_.has(props, "selectedVersion.gameConfig") || !props.areMediaLoaded || !props.areAtlasesLoaded)
  )
    return null;
  const classes = useStyles();
  const [newTabPanel, setTabPanel] = useState(classes.tabPanel);

  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState(_.get(props, "gameConfig.options.language"));
  const [removeLanguageDialog, setRemoveLanguageDialog] = useState({ open: false, languageKey: null });
  const swipeableViewRef = useRef(null);
  // Ignore spurious SwipeableViews index changes while programmatically opening Assets.
  const ignoreSwipeIndexRef = useRef(false);
  const undoCtx = useContext(UndoContext);

  // ── Preset state (playEngine only) ──────────────────────────────────────
  const [presets, setPresets] = useState([]);
  const [presetsLoaded, setPresetsLoaded] = useState(false);
  const [presetConfirmDialog, setPresetConfirmDialog] = useState({ open: false, pendingPreset: null });
  // Incremented each time a preset is applied so PlayEngineVersionSettings
  // knows the next playengine:ready should trigger a panel re-fetch.
  const [presetApplyCount, setPresetApplyCount] = useState(0);
  // Assets tab: scroll/highlight a row when linked from Version Settings.
  const [highlightAssetKey, setHighlightAssetKey] = useState(null);

  const navigateToAsset = useCallback((assetKey) => {
    if (!assetKey) return;
    setHighlightAssetKey(assetKey);
    ignoreSwipeIndexRef.current = true;
    setTabValue(1);
    window.setTimeout(() => {
      ignoreSwipeIndexRef.current = false;
    }, 700);
  }, []);

  // The agent panels bill the user's OWN company, not the one being looked at,
  // so they are handed that company's document rather than `userCompany` — which
  // follows a switch and would show a customer's credit balance to a Playin user
  // working inside it. Falls back to userCompany, which is the same document
  // whenever nobody has switched.
  const agentCompany = props.agentCompany || props.userCompany;
  const refreshAgentCompany = props.refreshAgentCompany || props.refreshCompany;

  const playEngineTabDefs = useMemo(() => {
    const defs = [
      { key: "versionSettings", label: "Version Settings", icon: <SettingsIcon /> },
      { key: "assets", label: "Assets", icon: <PhotoLibraryIcon /> },
      { key: "generalSettings", label: "General Settings", icon: <TuneIcon /> },
      { key: "customVariables", label: "Custom Variables", icon: <ListAltIcon /> },
      { key: "design", label: "Design", icon: <PaletteIcon /> },
      // ENGINE sounds — the global slots the playable declares. A component's
      // own "audio" dependency is a different thing and stays in Version
      // Settings, next to that component's other assets.
      { key: "sounds", label: "Sounds", icon: <VolumeUpIcon /> },
      // The version's embedded gameplay layer (`scripts[]`).
      { key: "scripts", label: "Scripts", icon: <CodeIcon /> }
    ];
    // Agent is offered to everyone and taken away by the narrowing below, which
    // is where the decision actually lives: "agent" is an opt-in tab, so a user
    // with no list of its own never sees it. It used to depend on the company
    // holding company.agent.usageAllowed, which broke the moment a user switched
    // into a company that had not bought one — the entitlement is the user's.
    defs.push({ key: "agent", label: "Agent", icon: <AssistantIcon /> });
    // Numbered 0..n AFTER narrowing, so the rail and the swipe panels stay in
    // step whichever tabs survive.
    return filterTabDefs(defs, "playEngine").map((tab, index) => ({ ...tab, index }));
  }, []);

  // One list for both editors that use it: the scene editor's icon rail and the
  // classic playable's tab bar have always shown exactly the same three tabs.
  const legacyTabDefs = useMemo(() => {
    const defs = [
      { key: "versionSettings", label: "Version Settings", icon: <SettingsIcon /> },
      { key: "localization", label: "Localization", icon: <LanguageIcon /> },
      { key: "generalSettings", label: "General Settings", icon: <TuneIcon /> },
    ];
    defs.push({ key: "agent", label: "Agent", icon: <AssistantIcon /> });
    return filterTabDefs(defs, "legacy").map((tab, index) => ({ ...tab, index }));
  }, []);

  useEffect(() => {
    if (!isPlayEngine) return;
    const maxTab = playEngineTabDefs.length - 1;
    if (tabValue > maxTab) setTabValue(maxTab);
  }, [isPlayEngine, playEngineTabDefs.length, tabValue]);

  useEffect(() => {
    if (isPlayEngine) return;
    const maxTab = legacyTabDefs.length - 1;
    if (tabValue > maxTab) setTabValue(maxTab);
  }, [isPlayEngine, legacyTabDefs.length, tabValue]);

  // Listen for agent-run completions: reload the iframe so the playEngine
  // picks up newly-added components, then arm a panel re-fetch on ready.
  useEffect(() => {
    if (!isPlayEngine) return;
    function handleAgentReload() {
      setPresetApplyCount((prev) => prev + 1);
      props.doRefresh((prev) => prev + 1);
    }
    function handleVersionsRefresh() {
      if (props.fetchVersions && props.selectedBase) {
        props.fetchVersions(String(props.selectedBase._id), localStorage.getItem("user.companyID"));
      }
    }
    window.addEventListener("playengine:agent-reload", handleAgentReload);
    window.addEventListener("playengine:agent-versions-refresh", handleVersionsRefresh);
    return () => {
      window.removeEventListener("playengine:agent-reload", handleAgentReload);
      window.removeEventListener("playengine:agent-versions-refresh", handleVersionsRefresh);
    };
  }, [isPlayEngine, props.doRefresh, props.fetchVersions, props.selectedBase]);
  const emptySavePresetDialog = {
    open: false,
    name: "",
    hasLinkedPreset: false,
    linkedPresetName: ""
  };
  const [savePresetDialog, setSavePresetDialog] = useState(emptySavePresetDialog);
  // Rename dialog state
  const [renameDialog, setRenameDialog] = useState({ open: false, preset: null, name: "" });
  // Delete confirm dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, preset: null });

  // Fetch presets from backend once when playEngine base is active
  useEffect(() => {
    if (!isPlayEngine) return;
    fetch(getServerUri("presetPlayEngine/find"), {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        accessToken: localStorage.getItem("session.accessToken")
      })
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
      .then((data) => {
        setPresets(data);
        setPresetsLoaded(true);
      })
      .catch((err) => console.error("Failed to fetch playEngine presets:", err));
  }, [isPlayEngine, _.get(props, "selectedBase._id")]);

  // One-time, on-load migration: convert dep-centric playEngineConfig to the
  // global asset-map format. Idempotent — once migrated, this is a no-op.
  // The migrated config is persisted through the normal save path.
  useEffect(() => {
    if (!isPlayEngine) return;
    const config = _.get(props, "selectedVersion.playEngineConfig");
    if (!needsAssetMapMigration(config)) return;
    const migrated = migrateConfigToAssetMap(config);
    console.info("[GameConfig] Migrated playEngineConfig to asset-map format.");
    props.setSelectedVersionPathValue({ path: "playEngineConfig", value: migrated });
  }, [isPlayEngine, _.get(props, "selectedVersion._id")]);

  /**
   * Check if the current version has been modified from its preset.
   * A version is considered "modified" if any component dependency has a
   * non-null source or sourceRef, meaning the user has uploaded assets.
   */
  const isVersionModified = useCallback(() => {
    const config = _.get(props, "selectedVersion.playEngineConfig", {});
    const components = _.get(config, "components", []);
    for (const comp of components) {
      for (const dep of comp.dependencies || []) {
        if (dep.source != null || dep.sourceRef != null) return true;
      }
    }
    return false;
  }, [props.selectedVersion]);

  /**
   * Apply a preset to the current version:
   * 1. Replace playEngineConfig with preset data + _presetId marker
   * 2. Persist to DB via setSelectedVersionPathValue
   * 3. Reload the playEngine iframe
   */
  const applyPreset = useCallback(
    (preset) => {
      if (!preset) return;
      const presetConfig = _.cloneDeep(preset.preset);
      const newConfig = { ...presetConfig, _presetId: String(preset._id) };
      const dashboardMeta = resolvePresetDashboardMeta({
        presetDashboardMeta: preset.dashboardMeta,
        playEngineConfig: presetConfig
      });
      props.setSelectedVersionPathValue({ path: "playEngineConfig", value: newConfig });
      props.setSelectedVersionPathValue({ path: "dashboardMeta", value: dashboardMeta });
      // Arm the one-shot re-fetch BEFORE reloading the iframe so the flag is
      // set by the time playengine:ready arrives (sync event — useEffect is too late).
      setPresetApplyCount((prev) => prev + 1);
      window.dispatchEvent(new CustomEvent("playengine:panel-refetch-arm"));
      // Reload the iframe so it re-reads the new snapshot
      props.doRefresh((prev) => prev + 1);
      if (props.createSnackbar) {
        props.createSnackbar(`Preset "${preset.name}" applied`, "success");
      }
    },
    [props.setSelectedVersionPathValue, props.doRefresh, props.createSnackbar]
  );

  /**
   * Create a brand-new version with the selected preset already applied.
   * Uses the same insert endpoint as DialogVersionInsert playEngine branch.
   */
  const createVersionWithPreset = useCallback(
    (preset) => {
      if (!preset) return;
      const presetConfig = _.cloneDeep(preset.preset);
      const presetConfigWithId = { ...presetConfig, _presetId: String(preset._id) };
      const dashboardMeta = resolvePresetDashboardMeta({
        presetDashboardMeta: preset.dashboardMeta,
        playEngineConfig: presetConfig
      });
      fetch(getServerUri("versionPlayEngine/insert"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          baseID: _.get(props, "selectedBase._id"),
          gameID: _.get(props, "selectedGame._id"),
          playEngineConfig: JSON.stringify(presetConfigWithId),
          dashboardMeta: JSON.stringify(dashboardMeta),
          title: `New Version (${preset.name})`
        })
      })
        .then((r) => {
          if (r.ok) return r.json();
          throw new Error("Insert failed: " + r.status);
        })
        .then((insertedRecord) => {
          // Use fetchVersions to reload the version list and pick the new version
          if (props.fetchVersions) {
            props.fetchVersions();
          }
          if (props.setSelectedVersion) {
            props.setSelectedVersion(insertedRecord);
          }
          if (props.createSnackbar) {
            props.createSnackbar(`New version created with preset "${preset.name}"`, "success");
          }
        })
        .catch((err) => {
          console.error("Failed to create version with preset:", err);
          if (props.createSnackbar) {
            props.createSnackbar("Failed to create version with preset", "error");
          }
        });
    },
    [props.selectedBase, props.selectedGame, props.fetchVersions, props.setSelectedVersion, props.createSnackbar]
  );

  /**
   * Handler when user selects a preset from the dropdown.
   * Shows a confirmation dialog if the current version has been modified.
   */
  const handlePresetSelect = useCallback(
    (presetId) => {
      if (!presetId || presetId === "") return;
      const preset = presets.find((p) => String(p._id) === String(presetId));
      if (!preset) return;

      const currentPresetId = _.get(props, "selectedVersion.playEngineConfig._presetId", "");
      // Already on this preset — nothing to do
      if (String(currentPresetId) === String(presetId)) return;

      if (isVersionModified()) {
        // Show confirmation dialog
        setPresetConfirmDialog({ open: true, pendingPreset: preset });
      } else {
        // Version is clean — apply directly
        applyPreset(preset);
      }
    },
    [presets, props.selectedVersion, isVersionModified, applyPreset]
  );

  /**
   * Save the current version's playEngineConfig as a new or updated preset.
   * mode: "create" inserts a new preset; "update" replaces the linked preset.
   * Either way, the version is linked to the resulting preset via _presetId.
   */
  const saveAsPreset = useCallback(
    (name, mode) => {
      const playEngineConfig = _.get(props, "selectedVersion.playEngineConfig", {});
      const dashboardMeta = buildPresetDashboardMeta({
        versionDashboardMeta: _.get(props, "selectedVersion.dashboardMeta", {}),
        playEngineConfig
      });
      const saveMode = mode === "update" ? "update" : "create";
      fetch(getServerUri("presetPlayEngine/save"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          name,
          mode: saveMode,
          playEngineConfig: JSON.stringify(playEngineConfig),
          dashboardMeta: JSON.stringify(dashboardMeta)
        })
      })
        .then((r) => (r.ok ? r.json() : r.json().then((d) => Promise.reject(d.error))))
        .then((saved) => {
          setPresets((prev) => {
            const idx = prev.findIndex((p) => String(p._id) === String(saved._id));
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = saved;
              return next;
            }
            return [...prev, saved];
          });
          props.setSelectedVersionPathValue({
            path: "playEngineConfig._presetId",
            value: String(saved._id)
          });
          const message = saveMode === "update" ? `Preset "${name}" updated` : `Preset "${name}" created`;
          props.createSnackbar?.(message, "success");
        })
        .catch((err) => {
          console.error("Failed to save preset:", err);
          props.createSnackbar?.("Failed to save preset", "error");
        });
    },
    [props.selectedVersion, props.setSelectedVersionPathValue, props.createSnackbar]
  );

  /**
   * Rename an existing preset.
   */
  const renamePreset = useCallback(
    (presetId, name) => {
      fetch(getServerUri("presetPlayEngine/rename"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          presetId,
          name
        })
      })
        .then((r) => (r.ok ? r.json() : r.json().then((d) => Promise.reject(d.error))))
        .then(() => {
          setPresets((prev) => prev.map((p) => (String(p._id) === String(presetId) ? { ...p, name } : p)));
          props.createSnackbar?.(`Preset renamed to "${name}"`, "success");
        })
        .catch((err) => {
          console.error("Failed to rename preset:", err);
          props.createSnackbar?.("Failed to rename preset", "error");
        });
    },
    [props.createSnackbar]
  );

  /**
   * Delete a preset.
   */
  const deletePreset = useCallback(
    (presetId) => {
      fetch(getServerUri("presetPlayEngine/delete"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          presetId
        })
      })
        .then((r) => (r.ok ? r.json() : r.json().then((d) => Promise.reject(d.error))))
        .then(() => {
          setPresets((prev) => prev.filter((p) => String(p._id) !== String(presetId)));
          props.createSnackbar?.("Preset deleted", "success");
        })
        .catch((err) => {
          console.error("Failed to delete preset:", err);
          props.createSnackbar?.("Failed to delete preset", "error");
        });
    },
    [props.createSnackbar]
  );
  // ── End preset state ────────────────────────────────────────────────────
  function isLocalizationLinkHas() {
    return _.has(props, "selectedVersion.gameConfig.options.localizationLink");
  }

  function isLocalizationLinkExistCurrentLanguage(path) {
    return !(
      _.get(props, `selectedVersion.gameConfig.options.localizationLink.${path}`) == undefined ||
      _.get(props, `selectedVersion.gameConfig.options.localizationLink.${path}`) == ""
    );
  }

  function buildGameConfigWithoutLanguage(languageKey) {
    const newGameConfig = _.cloneDeep(_.get(props, "selectedVersion.gameConfig"));
    _.unset(newGameConfig, `options.localizationLink.${languageKey}`);
    Object.keys(_.get(newGameConfig, "components", {})).forEach((componentID) => {
      _.unset(newGameConfig, `components.${componentID}.localization.${languageKey}`);
    });
    Object.keys(_.get(newGameConfig, "gameObjects", {})).forEach((gameObjectName) => {
      _.unset(newGameConfig, `gameObjects.${gameObjectName}.caption.${languageKey}`);
    });
    stripLanguageFromLocalizationOverrides(newGameConfig, languageKey);
    if (_.get(newGameConfig, "options.language") === languageKey) {
      _.set(newGameConfig, "options.language", "en");
    }
    return newGameConfig;
  }

  function removeCreatedLanguage(languageKey) {
    if (_.isNil(languageKey) || languageKey === "en") {
      return;
    }
    const createdLanguages = Array.isArray(_.get(props, "selectedVersion.createdLanguages"))
      ? [...props.selectedVersion.createdLanguages]
      : ["en"];
    const newCreatedLanguages = createdLanguages.filter((language) => language !== languageKey);
    if (!newCreatedLanguages.includes("en")) {
      newCreatedLanguages.unshift("en");
    }
    const newGameConfig = buildGameConfigWithoutLanguage(languageKey);
    const wasActive = _.get(props, "selectedVersion.gameConfig.options.language") === languageKey;
    props.setSelectedVersionPathValue({ path: "createdLanguages", value: newCreatedLanguages });
    props.setSelectedVersionPathValue({ path: "gameConfig", value: newGameConfig, isLanguageChanged: true });
    if (wasActive) {
      setSelectedLanguage("en");
    }
    const languageCaption = _.get(props, `selectedBase.supportedLanguages.${languageKey}`, languageKey);
    props.createSnackbar(`"${languageCaption}" language removed from this version`, "success");
  }

  useEffect(() => {
    setSelectedLanguage(_.get(props, "selectedVersion.gameConfig.options.language"));
    props.setLocalizationLanguage(_.get(props, "selectedVersion.gameConfig.options.language"));
  }, [props.selectedVersion?.gameConfig]);

  useEffect(() => {
    console.log("customDrag", props.selectedVersion?.title);
  }, []);
  useEffect(() => {
    // figured out safari scroll problem between tabs.
    if (swipeableViewRef && swipeableViewRef.current) {
      const swipeableViewRefNode = ReactDOM.findDOMNode(swipeableViewRef.current);
      const swipeableVievContainer = swipeableViewRefNode.querySelector(".react-swipeable-view-container");
      swipeableVievContainer.style.height = "100%";
    }
  });

  if (_.isNil(props.selectedGame) || _.isNil(props.selectedBase)) {
    return null;
  }

  function resetVersionSettings() {
    undoCtx.setUndoStack([]);
    undoCtx.setCursor(0);
    console.info("Resetting version settings");
    let newGameConfig = _.clone(props.selectedVersion.gameConfig);

    if (_.has(newGameConfig, "selectedCardOrder")) {
      _.set(newGameConfig, "selectedCardOrder", 0);
    }

    let destination;
    let defaultValue;

    const hasModifiedBase = _.has(props, "selectedModifiedBase");
    const defaultVersion = props.versions.filter((version) => version._id == props.defaultVersion?.versionID)[0];
    const selectedBaseString = hasModifiedBase ? "selectedModifiedBase" : "selectedBase";
    const activeBase = _.get(props, `${selectedBaseString}`);

    // if there is an array property it is emptied
    Object.entries(newGameConfig.components).forEach(([componentId, property]) => {
      Object.entries(property).forEach(([key, value]) => {
        if (_.isArray(value)) {
          const value = defaultVersion
            ? _.get(defaultVersion, `gameConfig.components.[${componentId}].${key}`, [])
            : _.get(props, `${selectedBaseString}.gameConfig.components.[${componentId}].${key}`, []);
          _.set(newGameConfig, `components.${componentId}.${key}`, value);
        }
      });
    });

    _.get(activeBase, "versionSettings.components", []).forEach((component) => {
      //if base does not have enemy1 and sounds component
      // todo this is NOT a good solution
      if (component.id !== "enemy1" && !_.includes(component.id, "sounds")) {
        _.get(component, "settings").forEach((setting) => {
          //we know our component id and what path to set

          //lets find our destination
          destination = `components.${component.id}.${setting.path}`;
          //lets find our default value
          if (defaultVersion) {
            defaultValue = _.get(defaultVersion, `gameConfig.components.[${component.id}].${setting.path}`);
          } else {
            defaultValue = _.get(
              props,
              `${selectedBaseString}.gameConfig.components.[${component.id}].${setting.path}`
            );
          }

          if (_.isNil(defaultValue)) {
            console.debug(
              `Resetting values. defaultValue is nil. Destination is: ${destination}. Returning WITHOUT setting`
            );
            return;
          }

          console.debug(
            `Resetting values. Destination: ${destination} Current Value: ${JSON.stringify(
              _.get(newGameConfig, destination)
            )} Default Value: ${JSON.stringify(defaultValue)}`
          );
          _.set(newGameConfig, destination, defaultValue);
        });
      }
    });

    //if base has enemy1 component
    if (!_.isNil(_.get(props, "selectedBase.gameConfig.components.enemy1"))) {
      destination = "components.enemy1.enemySoldiers";
      if (defaultVersion) {
        defaultValue = _.get(defaultVersion, "gameConfig.components.enemy1.enemySoldiers.enemySoldier1");
      } else {
        defaultValue = _.get(props, "selectedBase.gameConfig.components.enemy1.enemySoldiers.enemySoldier1");
      }
      _.set(newGameConfig, destination, { "enemySoldier1": defaultValue });
    }
    let defaultGameConfig;
    if (defaultVersion) {
      defaultGameConfig = props.addDefaultVersionImagesToGameConfig(newGameConfig, defaultVersion);
    } else {
      defaultGameConfig = props.addDefaultImagesToGameConfig(newGameConfig);
    }
    props.setSelectedVersionPathValue({ path: "gameConfig", value: defaultGameConfig }, true);
    // ugly way to rerender version settings TODO refactor
    props.setSelectedModifiedBase(null);
  }

  function resetSceneEditorCategorization() {
    const metadata = _.get(props, "selectedVersion.gameConfig.metadata");
    if (!hasOwnSceneEditorCategoryData(metadata)) {
      props.createSnackbar("This version has no custom categorization", "warning");
      return;
    }
    undoCtx.setUndoStack([]);
    undoCtx.setCursor(0);
    props.setSelectedVersionPathValue(
      {
        path: "gameConfig.metadata",
        value: stripSceneEditorCategoryLayout(metadata),
      },
      true
    );
    props.setSelectedModifiedBase(null);
  }

  function resetLocalizationSettings() {
    undoCtx.setUndoStack([]);
    undoCtx.setCursor(0);
    const defaultLanguage = "en";
    console.info("Resetting localization settings");

    const activeBase = _.has(props.selectedBase, "predefinedGameComponents")
      ? props.selectedModifiedBase
      : props.selectedBase;
    let newGameConfig = _.clone(props.selectedVersion.gameConfig);
    const defaultVersion = props.versions.filter((version) => version._id === props.defaultVersion?.versionID)[0];
    _.get(activeBase, "localizationSettings.components", []).forEach((component) => {
      _.get(component, "settings", []).forEach((setting) => {
        //we know our component id and what path to set

        //TODO: component.id can be used instead of gameComponentId
        //lets find our destination
        let destination = "";
        let defaultValue = null;

        // this condition is placed just for selectorRibbonType which is a parent component of components.
        if (_.has(component, "gameComponentId")) {
          destination = `components.${component.gameComponentId}.localization.${selectedLanguage}.${setting.path}`;

          if (defaultVersion) {
            defaultValue = _.get(
              defaultVersion,
              `gameConfig.components.[${component.gameComponentId}].localization.${selectedLanguage}.${setting.path}`
            );
          } else {
            defaultValue = _.get(
              activeBase,
              `gameConfig.components.[${component.gameComponentId}].localization.${defaultLanguage}.${setting.path}`
            );
          }
        } else {
          destination = `components.${component.id}.localization.${selectedLanguage}.${setting.path}`;
          console.log(
            "defaultVersionLocalization",
            _.get(defaultVersion, `gameConfig.components.[${component.id}].localization`),
            defaultLanguage,
            selectedLanguage
          );

          if (defaultVersion) {
            defaultValue = _.get(
              defaultVersion,
              `gameConfig.components.[${component.id}].localization.${selectedLanguage}.${setting.path}`
            );
          } else {
            defaultValue = _.get(
              activeBase,
              `gameConfig.components.[${component.id}].localization.${defaultLanguage}.${setting.path}`
            );
          }
        }
        console.debug(
          `Resetting values. Destination: ${destination} Current Value: ${JSON.stringify(
            _.get(newGameConfig, destination)
          )} Default Value: ${JSON.stringify(defaultValue)}`
        );
        _.set(newGameConfig, destination, defaultValue);
      });
    });

    props.setSelectedVersionPathValue({ path: "gameConfig", value: newGameConfig }, true);
  }

  function resetGeneralSettings() {
    undoCtx.setUndoStack([]);
    undoCtx.setCursor(0);
    console.info("Resetting general settings");

    let newGameConfig = _.clone(props.selectedVersion.gameConfig);

    console.info("Resetting options");

    let optionsValue;
    const defaultVersion = props.versions.filter((version) => version._id == props.defaultVersion?.versionID)[0];

    if (defaultVersion) {
      optionsValue = defaultVersion.gameConfig.options;
    } else {
      optionsValue = props.selectedBase.gameConfig.options;
    }

    _.set(newGameConfig, "options", _.cloneDeep(optionsValue));
    props.setSelectedVersionPathValue({ path: "gameConfig", value: newGameConfig }, true);
  }

  if (!isPlayEngine && _.isNil(props.selectedVersion?.gameConfig)) return null;

  const isVersionLocked = !isPlayEngine && props.selectedVersion?.isLocked === true;
  const effectiveUserPermissions = isVersionLocked
    ? { ...(props.userPermissions || {}), editContent: false }
    : props.userPermissions;

  function applySelectedLanguageChange(newLanguage) {
    if (effectiveUserPermissions["editContent"] === false) {
      props.createSnackbar(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    const newGameConfig = _.cloneDeep(_.get(props, "selectedVersion.gameConfig"));

    if (isLocalizationLinkHas() && !isLocalizationLinkExistCurrentLanguage(newLanguage)) {
      const path = `options.localizationLink.${newLanguage}`;
      const englishSettings = _.get(
        props,
        "selectedVersion.gameConfig.options.localizationLink.en",
        undefined
      );
      _.set(newGameConfig, path, englishSettings);
    }

    Object.entries(_.get(props, "selectedVersion.gameConfig.components", {})).forEach(
      ([componentID, detail]) => {
        if (_.has(detail, "localization")) {
          const englishSettings = _.get(
            props,
            `selectedVersion.gameConfig.components.${componentID}.localization.en`,
            undefined
          );
          if (!_.isNil(englishSettings)) {
            const path = `components.${componentID}.localization.${newLanguage}`;
            const existingSettings = _.get(detail, `localization.${newLanguage}`, {});
            _.set(newGameConfig, path, { ...englishSettings, ...existingSettings });
          }
        }
      }
    );
    ensureLocalizationOverrideLanguage(newGameConfig, newLanguage);
    setSelectedLanguage(newLanguage);
    _.set(newGameConfig, "options.language", newLanguage);
    const createdLanguages = Array.isArray(_.get(props, "selectedVersion.createdLanguages"))
      ? [...props.selectedVersion.createdLanguages]
      : ["en"];
    if (!createdLanguages.includes("en")) {
      createdLanguages.unshift("en");
    }
    if (!createdLanguages.includes(newLanguage)) {
      createdLanguages.push(newLanguage);
    }
    props.setSelectedVersionPathValue({
      path: "gameConfig",
      value: newGameConfig,
      isLanguageChanged: true
    });
    props.setSelectedVersionPathValue({ path: "createdLanguages", value: createdLanguages });
  }

  /**
   * Every playEngine tab used to be rendered on every pass, with the inactive
   * ones merely `hidden`. One edit therefore re-rendered all eight panels —
   * Version Settings alone is 6.6k lines — no matter which tab was on screen.
   *
   * A tab is now mounted the first time it is opened, and while it is off screen
   * the *same element object* is handed back to React, which bails out of that
   * subtree instead of re-rendering it.
   *
   * Freezing rather than unmounting is deliberate. Version Settings rebuilds
   * itself from the engine on mount (`get-dependencies`, then a `get-bucket` per
   * component), so unmounting would pay for those round trips on every tab
   * switch and drop selection and expansion state with them. Nothing unmounts
   * here, so timers, sockets and window listeners behind a hidden tab keep
   * running, and a panel's own state updates still re-render it — only
   * parent-driven renders are skipped. A frozen panel holding last version's
   * props is fine: opening it renders it with the current ones.
   */
  const playEnginePanelCacheRef = useRef(new Map());

  /**
   * The identity a cached panel body was built under — see `shouldRebuildPanel`.
   *
   * The selected version is the one that bites here. Version Settings clears
   * its component tree when the version changes and refills it from the engine
   * when the iframe announces `playengine:playable-ready`; frozen on the old
   * version it never learns of the change, lets the announcement pass, and then
   * clears itself when the tab is finally opened — reading "No components
   * found" until some later switch happened to catch it live. `presetApplyCount`
   * has the same shape: it arms a re-fetch for the reload a preset triggers.
   */
  const panelGeneration = [
    _.get(props, "selectedBase._id"),
    _.get(props, "selectedVersion._id"),
    presetApplyCount,
  ].join("|");

  // The agent is never frozen. It tracks which version is selected in order to
  // decide whether a finished run applies to what you are looking at, and it
  // learns that from its props — a frozen copy would still be holding the
  // version you left, and would apply that run's config to this one.
  const ALWAYS_LIVE_TABS = new Set(["agent"]);

  // Version Settings is also the one panel that cannot be mounted lazily: it has
  // only the engine's one-shot announcement to fetch on, where the others read
  // `isPreviewLoaded` and recover whenever they are opened. Usually free — it is
  // tab 0, so the first paint was building it anyway.
  const MOUNT_EAGERLY_TABS = new Set(["versionSettings"]);

  function renderPlayEngineSwipePanel(tab) {
    const cache = playEnginePanelCacheRef.current;
    const cached = cache.get(tab.key) || null;

    if (
      shouldRebuildPanel({
        cached,
        generation: panelGeneration,
        isActive: tabValue === tab.index,
        isLive: ALWAYS_LIVE_TABS.has(tab.key),
        mountEagerly: MOUNT_EAGERLY_TABS.has(tab.key),
      })
    ) {
      cache.set(tab.key, { generation: panelGeneration, body: renderPlayEnginePanelBody(tab) });
    }

    const entry = cache.get(tab.key);
    // Never opened: not mounted at all, so the editor's first paint builds one
    // panel instead of eight.
    if (!entry) return null;

    return (
      <TabPanel key={tab.key} value={tabValue} index={tab.index} className={classes.tabPanel}>
        {entry.body}
      </TabPanel>
    );
  }

  function renderPlayEnginePanelBody(tab) {
    switch (tab.key) {
      case "versionSettings":
        return (
          <PlayEngineVersionSettings
            selectedVersion={props.selectedVersion}
            versions={props.versions}
            selectedBase={props.selectedBase}
            selectedGame={props.selectedGame}
            iFrameId={props.iFrameId}
            setSelectedVersionPathValue={props.setSelectedVersionPathValue}
            userPermissions={props.userPermissions}
            createSnackbar={props.createSnackbar}
            media={props.media}
            atlases={props.atlases}
            bitmapFonts={props.bitmapFonts}
            sounds={props.sounds}
            fonts={props.fonts}
            videos={props.videos}
            insertMediaPathValue={props.insertMediaPathValue}
            insertAtlasPathValue={props.insertAtlasPathValue}
            insertBitmapFontPathValue={props.insertBitmapFontPathValue}
            insertSoundPathValue={props.insertSoundPathValue}
            insertFontPathValue={props.insertFontPathValue}
            insertVideoPathValue={props.insertVideoPathValue}
            setIsDialogCircularProgressOpen={props.setIsDialogCircularProgressOpen}
            localizationLanguage={props.localizationLanguage}
            setLocalizationLanguage={props.setLocalizationLanguage}
            presetApplyCount={presetApplyCount}
            newSetPortrait={props.newSetPortrait}
            onNavigateToAsset={navigateToAsset}
            deleteImageFromMedia={props.deleteImageFromMedia}
            deleteFontFromFonts={props.deleteFontFromFonts}
            deleteSoundFromSounds={props.deleteSoundFromSounds}
            deleteVideoFromVideos={props.deleteVideoFromVideos}
            deleteAnimationSetFromAtlases={props.deleteAnimationSetFromAtlases}
            deleteFontSetFromBitmapFonts={props.deleteFontSetFromBitmapFonts}
          />
        );
      case "assets":
        return (
          <PlayEngineAssets
            selectedVersion={props.selectedVersion}
            versions={props.versions}
            selectedBase={props.selectedBase}
            selectedGame={props.selectedGame}
            iFrameId={props.iFrameId}
            setSelectedVersionPathValue={props.setSelectedVersionPathValue}
            userPermissions={props.userPermissions}
            createSnackbar={props.createSnackbar}
            media={props.media}
            atlases={props.atlases}
            bitmapFonts={props.bitmapFonts}
            sounds={props.sounds}
            fonts={props.fonts}
            videos={props.videos}
            insertMediaPathValue={props.insertMediaPathValue}
            insertAtlasPathValue={props.insertAtlasPathValue}
            insertBitmapFontPathValue={props.insertBitmapFontPathValue}
            insertSoundPathValue={props.insertSoundPathValue}
            insertFontPathValue={props.insertFontPathValue}
            insertVideoPathValue={props.insertVideoPathValue}
            isPreviewLoaded={props.isPreviewLoaded}
            highlightAssetKey={highlightAssetKey}
            onHighlightDone={() => setHighlightAssetKey(null)}
            onNavigateToAsset={navigateToAsset}
            deleteImageFromMedia={props.deleteImageFromMedia}
            deleteFontFromFonts={props.deleteFontFromFonts}
            deleteSoundFromSounds={props.deleteSoundFromSounds}
            deleteVideoFromVideos={props.deleteVideoFromVideos}
            deleteAnimationSetFromAtlases={props.deleteAnimationSetFromAtlases}
            deleteFontSetFromBitmapFonts={props.deleteFontSetFromBitmapFonts}
          />
        );
      case "generalSettings":
        return (
          <PlayEngineGeneralSettings
            selectedVersion={props.selectedVersion}
            selectedBase={props.selectedBase}
            selectedGame={props.selectedGame}
            setSelectedVersionPathValue={props.setSelectedVersionPathValue}
            userPermissions={props.userPermissions}
            createSnackbar={props.createSnackbar}
            isPreviewLoaded={props.isPreviewLoaded}
            iFrameId={props.iFrameId}
          />
        );
      case "scripts":
        return (
          <PlayEngineScripts
            selectedVersion={props.selectedVersion}
            selectedBase={props.selectedBase}
            selectedGame={props.selectedGame}
            setSelectedVersionPathValue={props.setSelectedVersionPathValue}
            userPermissions={props.userPermissions}
            createSnackbar={props.createSnackbar}
            isPreviewLoaded={props.isPreviewLoaded}
            iFrameId={props.iFrameId}
          />
        );
      case "customVariables":
        return (
          <PlayEngineCustomVariables
            selectedVersion={props.selectedVersion}
            selectedBase={props.selectedBase}
            selectedGame={props.selectedGame}
            setSelectedVersionPathValue={props.setSelectedVersionPathValue}
            userPermissions={props.userPermissions}
            createSnackbar={props.createSnackbar}
            isPreviewLoaded={props.isPreviewLoaded}
            iFrameId={props.iFrameId}
            localizationLanguage={props.localizationLanguage}
            setLocalizationLanguage={props.setLocalizationLanguage}
          />
        );
      case "design":
        return (
          <PlayEngineDesign
            selectedVersion={props.selectedVersion}
            selectedBase={props.selectedBase}
            selectedGame={props.selectedGame}
            setSelectedVersionPathValue={props.setSelectedVersionPathValue}
            // A boolean, not a setter, despite the name: which orientation the
            // preview is in, and therefore which half of a responsive value the
            // design rows edit.
            newSetPortrait={props.newSetPortrait}
            // An asset row links to its slot in the Assets tab, and — with no
            // assetProvider here, deliberately, for the reason given below —
            // sends a replace there too.
            onNavigateToAsset={navigateToAsset}
            userPermissions={props.userPermissions}
            createSnackbar={props.createSnackbar}
            iFrameId={props.iFrameId}
            isPreviewLoaded={props.isPreviewLoaded}
            localizationLanguage={props.localizationLanguage}
            setLocalizationLanguage={props.setLocalizationLanguage}
          />
        );
      // No assetProvider here on purpose: without one the panel defaults to
      // hasLibrary, which is right for the version editor — slots reference the
      // mongo media library rather than embedding bytes.
      case "sounds":
        return (
          <PlayEngineSounds
            selectedVersion={props.selectedVersion}
            setSelectedVersionPathValue={props.setSelectedVersionPathValue}
            sounds={props.sounds}
            insertSoundPathValue={props.insertSoundPathValue}
            deleteSoundFromSounds={props.deleteSoundFromSounds}
            userPermissions={props.userPermissions}
            createSnackbar={props.createSnackbar}
            iFrameId={props.iFrameId}
          />
        );
      case "agent":
        return (
          <PlayEngineAgent
            selectedVersion={props.selectedVersion}
            selectedBase={props.selectedBase}
            versions={props.versions}
            createSnackbar={props.createSnackbar}
            setSelectedVersionPathValue={props.setSelectedVersionPathValue}
            isAdmin={props.isAdmin}
            userCompany={agentCompany}
            refreshCompany={refreshAgentCompany}
          />
        );
      default:
        return null;
    }
  }

  // ── PLAYENGINE BRANCH ─────────────────────────────────────────────────────
  if (isPlayEngine) {
    // Match Version Settings panel palette (containerPlayEngineVersionSettings).
    const PANEL_BG = "#2b2b2e";
    const SECTION_DIVIDER = "#3a3a3f";
    const INPUT_BG = "#212226";
    const CARD_BG = "#474747";
    const currentPresetId = _.get(props, "selectedVersion.playEngineConfig._presetId", "");
    return (
      <div id={"gameConfigContainerDiv"} className={classes.gameConfigContainerDiv}>
        {/* ── Preset Selector ───────────────────────────────────────── */}
        {presetsLoaded && (
          <div
            style={{
              padding: "10px 12px",
              borderBottom: `1px solid ${SECTION_DIVIDER}`,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              backgroundColor: PANEL_BG,
            }}
          >
            <Typography style={{ color: "#a7abb2", flexShrink: 0, fontSize: "13px", fontWeight: 500 }}>
              Preset
            </Typography>
            <FormControl size="small" style={{ flex: 1 }}>
              <Select
                displayEmpty
                value={currentPresetId || ""}
                onChange={(e) => handlePresetSelect(e.target.value)}
                style={{
                  color: "#fff",
                  fontSize: "13px",
                  backgroundColor: INPUT_BG,
                  borderRadius: 4,
                  padding: "2px 4px",
                }}
                MenuProps={{ PaperProps: { style: { backgroundColor: CARD_BG } } }}
                renderValue={(val) => {
                  if (!val) return <em style={{ color: "#888" }}>No Preset</em>;
                  const p = presets.find((x) => String(x._id) === String(val));
                  return p ? p.name : <em style={{ color: "#888" }}>No Preset</em>;
                }}
              >
                <MenuItem value="">
                  <em>No Preset</em>
                </MenuItem>
                {presets.map((p) => (
                  <MenuItem
                    key={p._id}
                    value={String(p._id)}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "4px" }}
                  >
                    <span style={{ flex: 1 }}>{p.name}</span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRenameDialog({ open: true, preset: p, name: p.name });
                      }}
                      style={{ padding: "2px", color: "#90caf9" }}
                    >
                      <EditIcon style={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteDialog({ open: true, preset: p });
                      }}
                      style={{ padding: "2px", color: "#ef9a9a" }}
                    >
                      <DeleteIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              title="Save current version as preset"
              onClick={() => {
                const presetId = _.get(props, "selectedVersion.playEngineConfig._presetId", "");
                const linkedPreset = presets.find((p) => String(p._id) === String(presetId));
                const hasLinkedPreset = Boolean(linkedPreset);
                const defaultName = hasLinkedPreset
                  ? linkedPreset.name
                  : _.get(props, "selectedVersion.title", "New Preset");
                setSavePresetDialog({
                  open: true,
                  name: defaultName,
                  hasLinkedPreset,
                  linkedPresetName: linkedPreset?.name || ""
                });
              }}
              style={{ color: "#a5d6a7", flexShrink: 0 }}
            >
              <SaveIcon style={{ fontSize: 18 }} />
            </IconButton>
          </div>
        )}
        {/* ── End Preset Selector ───────────────────────────────────── */}


        {/* ── Save as Preset dialog ─────────────────────────────────── */}
        <Dialog
          open={savePresetDialog.open}
          onClose={() => setSavePresetDialog(emptySavePresetDialog)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Save as Preset</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Preset name"
              value={savePresetDialog.name}
              onChange={(e) => {
                const v = e.target.value;
                setSavePresetDialog((s) => ({ ...s, name: v }));
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter" && savePresetDialog.name.trim()) {
                  const trimmedName = savePresetDialog.name.trim();
                  const createName =
                    savePresetDialog.hasLinkedPreset && trimmedName === savePresetDialog.linkedPresetName
                      ? `${trimmedName} (copy)`
                      : trimmedName;
                  saveAsPreset(createName, "create");
                  setSavePresetDialog(emptySavePresetDialog);
                }
              }}
              style={{ marginBottom: savePresetDialog.hasLinkedPreset ? 12 : 0 }}
            />
            {savePresetDialog.hasLinkedPreset && (
              <Typography variant="body2" style={{ color: "#bbb" }}>
                Linked to preset &ldquo;{savePresetDialog.linkedPresetName}&rdquo;. Choose whether to update it or save
                a new copy.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSavePresetDialog(emptySavePresetDialog)}>Cancel</Button>
            {savePresetDialog.hasLinkedPreset ? (
              <>
                <Button
                  disabled={!savePresetDialog.name.trim()}
                  onClick={() => {
                    saveAsPreset(savePresetDialog.name.trim(), "update");
                    setSavePresetDialog(emptySavePresetDialog);
                  }}
                >
                  {`Update "${savePresetDialog.linkedPresetName}"`}
                </Button>
                <Button
                  color="primary"
                  disabled={!savePresetDialog.name.trim()}
                  onClick={() => {
                    const trimmedName = savePresetDialog.name.trim();
                    const createName =
                      trimmedName === savePresetDialog.linkedPresetName ? `${trimmedName} (copy)` : trimmedName;
                    saveAsPreset(createName, "create");
                    setSavePresetDialog(emptySavePresetDialog);
                  }}
                >
                  Save as new
                </Button>
              </>
            ) : (
              <Button
                color="primary"
                disabled={!savePresetDialog.name.trim()}
                onClick={() => {
                  saveAsPreset(savePresetDialog.name.trim(), "create");
                  setSavePresetDialog(emptySavePresetDialog);
                }}
              >
                Save
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* ── Rename preset dialog ──────────────────────────────────── */}
        <Dialog
          open={renameDialog.open}
          onClose={() => setRenameDialog({ open: false, preset: null, name: "" })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Rename Preset</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="New name"
              value={renameDialog.name}
              onChange={(e) => {
                const v = e.target.value;
                setRenameDialog((s) => ({ ...s, name: v }));
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter" && renameDialog.preset) {
                  const name = e.target.value.trim();
                  if (name) {
                    renamePreset(String(renameDialog.preset._id), name);
                    setRenameDialog({ open: false, preset: null, name: "" });
                  }
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRenameDialog({ open: false, preset: null, name: "" })}>Cancel</Button>
            <Button
              color="primary"
              disabled={!renameDialog.name.trim()}
              onClick={() => {
                renamePreset(String(renameDialog.preset._id), renameDialog.name.trim());
                setRenameDialog({ open: false, preset: null, name: "" });
              }}
            >
              Rename
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Delete preset confirm dialog ──────────────────────────── */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, preset: null })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Preset</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Delete preset <strong>{deleteDialog.preset?.name}</strong>? This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, preset: null })}>Cancel</Button>
            <Button
              style={{ color: "#f44336" }}
              onClick={() => {
                deletePreset(String(deleteDialog.preset._id));
                setDeleteDialog({ open: false, preset: null });
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Blender-style vertical icon rail (left) + content (right).
            Base background matches the Version Settings panel (PANEL_BG)
            so every playEngine tab shares one surface color. */}
        <div style={{ display: "flex", flex: 1, minHeight: 0, backgroundColor: PANEL_BG }}>
          <div
            role="tablist"
            style={{
              display: "flex",
              flexDirection: "column",
              width: 44,
              flexShrink: 0,
              overflow: "hidden",
              borderRight: `1px solid ${SECTION_DIVIDER}`,
            }}
          >
            {playEngineTabDefs.map((tab) => {
              const selected = tabValue === tab.index;
              return (
                <Tooltip key={tab.key} title={tab.label} placement="right">
                  <IconButton
                    role="tab"
                    aria-selected={selected}
                    aria-label={tab.label}
                    id={`simple-tab-${tab.index}`}
                    aria-controls={`simple-tabpanel-${tab.index}`}
                    onClick={() => setTabValue(tab.index)}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 0,
                      color: selected ? theme.palette.primary.main : "#bbb",
                      opacity: selected ? 1 : 0.7,
                      borderLeft: selected
                        ? `2px solid ${theme.palette.primary.main}`
                        : "2px solid transparent",
                      backgroundColor: selected ? "rgba(144, 202, 249, 0.12)" : "transparent",
                    }}
                  >
                    {tab.icon}
                  </IconButton>
                </Tooltip>
              );
            })}
          </div>
          <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            {playEngineTabDefs.map(renderPlayEngineSwipePanel)}
          </div>
        </div>

        {/* ── Preset Confirmation Dialog ───────────────────────────── */}
        <Dialog
          open={presetConfirmDialog.open}
          onClose={() => setPresetConfirmDialog({ open: false, pendingPreset: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle style={{ textAlign: "center" }}>Attention</DialogTitle>
          <DialogContent>
            <Typography style={{ textAlign: "center", marginBottom: "8px" }}>
              Changing preset will erase all your data on this version.
            </Typography>
            <Typography style={{ textAlign: "center", color: "#999", fontSize: "13px" }}>
              You can choose to apply the preset anyway, create a new version with the selected preset, or cancel.
            </Typography>
          </DialogContent>
          <DialogActions style={{ justifyContent: "center", padding: "16px", gap: "8px" }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                applyPreset(presetConfirmDialog.pendingPreset);
                setPresetConfirmDialog({ open: false, pendingPreset: null });
              }}
            >
              Change Preset Anyway
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                createVersionWithPreset(presetConfirmDialog.pendingPreset);
                setPresetConfirmDialog({ open: false, pendingPreset: null });
              }}
            >
              Create New Version
            </Button>
            <Button variant="outlined" onClick={() => setPresetConfirmDialog({ open: false, pendingPreset: null })}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
        {/* ── End Preset Confirmation Dialog ────────────────────────── */}
      </div>
    );
  }
  // ── END PLAYENGINE BRANCH ──────────────────────────────────────────────────

  const SCENE_EDITOR_PANEL_BG = "#2b2b2e";
  const SCENE_EDITOR_SECTION_DIVIDER = "#3a3a3f";

  // Keyed rather than positional: a user can be narrowed to a subset of the
  // tabs, so the panels are looked up by key and numbered from whatever
  // legacyTabDefs ended up containing.
  const normalPanelsByKey = {
    versionSettings: (
      <>
      {isSceneEditor ? (
        <SceneEditor
          selectedVersion={props.selectedVersion}
          selectedBase={props.selectedBase}
          selectedModifiedBase={props.selectedModifiedBase}
          setSelectedModifiedBase={props.setSelectedModifiedBase}
          setTotalSize={props.setTotalSize}
          selectedGame={props.selectedGame}
          classes={props.classes}
          media={props.media}
          atlases={props.atlases}
          spines={props.spines}
          sounds={props.sounds}
          newSetPortrait={props.newSetPortrait}
          videos={props.videos}
          fonts={props.fonts}
          glbs={props.glbs}
          areMediaLoaded={props.areMediaLoaded}
          areAtlasesLoaded={props.areAtlasesLoaded}
          areSpinesLoaded={props.areSpinesLoaded}
          areSoundsLoaded={props.areSoundsLoaded}
          areVideosLoaded={props.areVideosLoaded}
          areFontsLoaded={props.areFontsLoaded}
          setSelectedVersionPathValue={props.setSelectedVersionPathValue}
          insertMediaPathValue={props.insertMediaPathValue}
          insertAtlasPathValue={props.insertAtlasPathValue}
          insertSpinePathValue={props.insertSpinePathValue}
          insertSoundPathValue={props.insertSoundPathValue}
          insertVideoPathValue={props.insertVideoPathValue}
          insertFontPathValue={props.insertFontPathValue}
          insertGlbPathValue={props.insertGlbPathValue}
          deleteImageFromMedia={props.deleteImageFromMedia}
          deleteImageSetFromMedia={props.deleteImageSetFromMedia}
          deleteFontFromFonts={props.deleteFontFromFonts}
          deleteSoundFromSounds={props.deleteSoundFromSounds}
          deleteVideoFromVideos={props.deleteVideoFromVideos}
          deleteAnimationSetFromAtlases={props.deleteAnimationSetFromAtlases}
          deleteAnimationSetFromSpines={props.deleteAnimationSetFromSpines}
          deleteGlbFromGlbs={props.deleteGlbFromGlbs}
          createSnackbar={props.createSnackbar}
          userPermissions={effectiveUserPermissions}
          versionRename={props.versionRename}
          userCompany={props.userCompany}
          setIsDialogConfirmRefreshOpen={props.setIsDialogConfirmRefreshOpen}
          setIsDialogCircularProgressOpen={props.setIsDialogCircularProgressOpen}
          setDefaultVersion={(value) => {
            props.setDefaultVersion(value);
          }}
          setDefaultVersionUpdated={(value) => {
            props.setDefaultVersionUpdated(value);
          }}
          defaultVersion={props.defaultVersion}
          getDefaultVersion={props.getDefaultVersion}
          defaultVersionUpdated={props.defaultVersionUpdated}
          searchBarValue={props.searchBarValue}
          setSearchBarValue={(value) => {
            props.setSearchBarValue(value);
          }}
          selectedLanguage={selectedLanguage}
          onNavigateToLocalization={() => setTabValue(1)}
          predefinedGameComponents={props.predefinedGameComponents}
          versions={props.versions}
          persistVersionPathValue={props.persistVersionPathValue}
          isVersionLocked={isVersionLocked}
        />
      ) : _.has(props.selectedBase, "predefinedGameComponents") ? (
        <VersionSettingsContainerTreeView
          selectedVersion={props.selectedVersion}
          selectedBase={props.selectedBase}
          selectedModifiedBase={props.selectedModifiedBase}
          setSelectedModifiedBase={props.setSelectedModifiedBase}
          setTotalSize={props.setTotalSize}
          selectedGame={props.selectedGame}
          classes={props.classes}
          media={props.media}
          atlases={props.atlases}
          spines={props.spines}
          sounds={props.sounds}
          newSetPortrait={props.newSetPortrait}
          videos={props.videos}
          fonts={props.fonts}
          glbs={props.glbs}
          areMediaLoaded={props.areMediaLoaded}
          areAtlasesLoaded={props.areAtlasesLoaded}
          areSpinesLoaded={props.areSpinesLoaded}
          areSoundsLoaded={props.areSoundsLoaded}
          areVideosLoaded={props.areVideosLoaded}
          areFontsLoaded={props.areFontsLoaded}
          setSelectedVersionPathValue={props.setSelectedVersionPathValue}
          insertMediaPathValue={props.insertMediaPathValue}
          insertAtlasPathValue={props.insertAtlasPathValue}
          insertSpinePathValue={props.insertSpinePathValue}
          insertSoundPathValue={props.insertSoundPathValue}
          insertVideoPathValue={props.insertVideoPathValue}
          insertFontPathValue={props.insertFontPathValue}
          insertGlbPathValue={props.insertGlbPathValue}
          deleteImageFromMedia={props.deleteImageFromMedia}
          deleteImageSetFromMedia={props.deleteImageSetFromMedia}
          deleteFontFromFonts={props.deleteFontFromFonts}
          deleteSoundFromSounds={props.deleteSoundFromSounds}
          deleteVideoFromVideos={props.deleteVideoFromVideos}
          deleteAnimationSetFromAtlases={props.deleteAnimationSetFromAtlases}
          deleteAnimationSetFromSpines={props.deleteAnimationSetFromSpines}
          deleteGlbFromGlbs={props.deleteGlbFromGlbs}
          createSnackbar={props.createSnackbar}
          userPermissions={effectiveUserPermissions}
          versionRename={props.versionRename}
          userCompany={props.userCompany}
          setIsDialogConfirmRefreshOpen={props.setIsDialogConfirmRefreshOpen}
          setIsDialogCircularProgressOpen={props.setIsDialogCircularProgressOpen}
          setDefaultVersion={(value) => {
            props.setDefaultVersion(value);
          }}
          setDefaultVersionUpdated={(value) => {
            props.setDefaultVersionUpdated(value);
          }}
          defaultVersion={props.defaultVersion}
          getDefaultVersion={props.getDefaultVersion}
          defaultVersionUpdated={props.defaultVersionUpdated}
          searchBarValue={props.searchBarValue}
          setSearchBarValue={(value) => {
            props.setSearchBarValue(value);
          }}
          selectedLanguage={selectedLanguage}
          onNavigateToLocalization={() => setTabValue(1)}
        ></VersionSettingsContainerTreeView>
      ) : (
        <VersionSettingsContainer
          selectedVersion={props.selectedVersion}
          selectedBase={props.selectedBase}
          setTotalSize={props.setTotalSize}
          selectedGame={props.selectedGame}
          classes={props.classes}
          media={props.media}
          atlases={props.atlases}
          spines={props.spines}
          sounds={props.sounds}
          newSetPortrait={props.newSetPortrait}
          videos={props.videos}
          fonts={props.fonts}
          areMediaLoaded={props.areMediaLoaded}
          areAtlasesLoaded={props.areAtlasesLoaded}
          areSpinesLoaded={props.areSpinesLoaded}
          areSoundsLoaded={props.areSoundsLoaded}
          areVideosLoaded={props.areVideosLoaded}
          areFontsLoaded={props.areFontsLoaded}
          setSelectedVersionPathValue={props.setSelectedVersionPathValue}
          insertMediaPathValue={props.insertMediaPathValue}
          insertAtlasPathValue={props.insertAtlasPathValue}
          insertSpinePathValue={props.insertSpinePathValue}
          insertSoundPathValue={props.insertSoundPathValue}
          insertVideoPathValue={props.insertVideoPathValue}
          insertFontPathValue={props.insertFontPathValue}
          deleteImageFromMedia={props.deleteImageFromMedia}
          deleteImageSetFromMedia={props.deleteImageSetFromMedia}
          deleteFontFromFonts={props.deleteFontFromFonts}
          deleteSoundFromSounds={props.deleteSoundFromSounds}
          deleteVideoFromVideos={props.deleteVideoFromVideos}
          deleteAnimationSetFromAtlases={props.deleteAnimationSetFromAtlases}
          deleteAnimationSetFromSpines={props.deleteAnimationSetFromSpines}
          createSnackbar={props.createSnackbar}
          userPermissions={effectiveUserPermissions}
          versionRename={props.versionRename}
          userCompany={props.userCompany}
          setIsDialogConfirmRefreshOpen={props.setIsDialogConfirmRefreshOpen}
          setIsDialogCircularProgressOpen={props.setIsDialogCircularProgressOpen}
          setDefaultVersion={(value) => {
            props.setDefaultVersion(value);
          }}
          setDefaultVersionUpdated={(value) => {
            props.setDefaultVersionUpdated(value);
          }}
          defaultVersion={props.defaultVersion}
          getDefaultVersion={props.getDefaultVersion}
          defaultVersionUpdated={props.defaultVersionUpdated}
          searchBarValue={props.searchBarValue}
          setSearchBarValue={(value) => {
            props.setSearchBarValue(value);
          }}
          selectedLanguage={selectedLanguage}
          onNavigateToLocalization={() => setTabValue(1)}
        ></VersionSettingsContainer>
      )}
      {!isSceneEditor && (
        <Card className={props.classes.card}>
          <CardContent className={props.classes.cardContent}>
            <Button
              id={"resetVersionSettings"}
              style={{ marginTop: "10px" }}
              disabled={isVersionLocked}
              onClick={() => {
                if (effectiveUserPermissions["editContent"] === false) {
                  props.createSnackbar(
                    "You do not have permission to update, contact your administrator.",
                    "warning",
                    false
                  );
                  return;
                }
                resetVersionSettings();
                if (props.defaultVersion?.versionID == props.selectedVersion._id) {
                  props.createSnackbar("There are no changes in the version", "warning");
                }
              }}
            >
              RESET VERSION SETTINGS
            </Button>
          </CardContent>
        </Card>
      )}
      </>
    ),
    localization: (
      <>
      <Dialog
        open={removeLanguageDialog.open}
        onClose={() => setRemoveLanguageDialog({ open: false, languageKey: null })}
        maxWidth="xs"
      >
        <DialogTitle disableTypography style={{ textAlign: "center" }}>
          <Typography variant="h6">Remove Language?</Typography>
        </DialogTitle>
        <div style={{ padding: "0 24px 8px", textAlign: "center" }}>
          <Typography>
            <b>
              {`"${_.get(
                props,
                `selectedBase.supportedLanguages.${removeLanguageDialog.languageKey}`,
                removeLanguageDialog.languageKey
              )}"`}
            </b>
            {" language and all of its localization data will be removed from this version. "}
            <b>This cannot be undone.</b>
            {" Continue?"}
          </Typography>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: "16px", padding: "16px" }}>
          <Button
            variant="contained"
            style={{ backgroundColor: "#f44336", color: "#ffffff", fontWeight: "bold" }}
            onClick={() => setRemoveLanguageDialog({ open: false, languageKey: null })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#418989", color: "#ffffff", fontWeight: "bold" }}
            onClick={() => {
              removeCreatedLanguage(removeLanguageDialog.languageKey);
              setRemoveLanguageDialog({ open: false, languageKey: null });
            }}
          >
            Remove
          </Button>
        </div>
      </Dialog>
      {isSceneEditor ? (
        <SceneEditor
          mode="localization"
          selectedVersion={props.selectedVersion}
          selectedBase={props.selectedBase}
          selectedModifiedBase={props.selectedModifiedBase}
          setSelectedModifiedBase={props.setSelectedModifiedBase}
          setTotalSize={props.setTotalSize}
          selectedGame={props.selectedGame}
          classes={props.classes}
          media={props.media}
          atlases={props.atlases}
          spines={props.spines}
          sounds={props.sounds}
          newSetPortrait={props.newSetPortrait}
          videos={props.videos}
          fonts={props.fonts}
          glbs={props.glbs}
          areMediaLoaded={props.areMediaLoaded}
          areAtlasesLoaded={props.areAtlasesLoaded}
          areSpinesLoaded={props.areSpinesLoaded}
          areSoundsLoaded={props.areSoundsLoaded}
          areVideosLoaded={props.areVideosLoaded}
          areFontsLoaded={props.areFontsLoaded}
          setSelectedVersionPathValue={props.setSelectedVersionPathValue}
          insertMediaPathValue={props.insertMediaPathValue}
          insertAtlasPathValue={props.insertAtlasPathValue}
          insertSpinePathValue={props.insertSpinePathValue}
          insertSoundPathValue={props.insertSoundPathValue}
          insertVideoPathValue={props.insertVideoPathValue}
          insertFontPathValue={props.insertFontPathValue}
          insertGlbPathValue={props.insertGlbPathValue}
          deleteImageFromMedia={props.deleteImageFromMedia}
          deleteImageSetFromMedia={props.deleteImageSetFromMedia}
          deleteFontFromFonts={props.deleteFontFromFonts}
          deleteSoundFromSounds={props.deleteSoundFromSounds}
          deleteVideoFromVideos={props.deleteVideoFromVideos}
          deleteAnimationSetFromAtlases={props.deleteAnimationSetFromAtlases}
          deleteAnimationSetFromSpines={props.deleteAnimationSetFromSpines}
          deleteGlbFromGlbs={props.deleteGlbFromGlbs}
          createSnackbar={props.createSnackbar}
          userPermissions={effectiveUserPermissions}
          userCompany={props.userCompany}
          selectedLanguage={selectedLanguage}
          predefinedGameComponents={props.predefinedGameComponents}
          versions={props.versions}
          persistVersionPathValue={props.persistVersionPathValue}
          defaultVersion={props.defaultVersion}
          isVersionLocked={isVersionLocked}
          onLanguageChange={(languageKey) => applySelectedLanguageChange(languageKey)}
          onRemoveLanguage={() => {
            if (effectiveUserPermissions["editContent"] === false) {
              props.createSnackbar(
                "You do not have permission to update, contact your administrator.",
                "warning",
                false
              );
              return;
            }
            setRemoveLanguageDialog({ open: true, languageKey: selectedLanguage });
          }}
          localizationHeader={
            Object.keys(_.get(props, "selectedVersion.gameConfig.gameObjects", {})).length > 0 ? (
              <TextGameObjectEditorContainer
                selectedBase={props.selectedBase}
                selectedVersion={props.selectedVersion}
                selectedLanguage={selectedLanguage}
                classes={props.classes}
                setSelectedVersionPathValue={props.setSelectedVersionPathValue}
                userPermissions={effectiveUserPermissions}
              />
            ) : null
          }
        />
      ) : (
        <>
          <Card className={props.classes.card}>
            <CardContent>
              <Typography className={props.classes.title} variant={"h6"} gutterBottom>
                {props.selectedVersion?.createdLanguages?.length === undefined
                  ? "Language"
                  : "Language - Active (" + props.selectedVersion?.createdLanguages?.length + ")"}
              </Typography>
              <div style={{ display: "flex", alignItems: "flex-end", gap: "4px" }}>
                <TextField
                  select
                  name={"selectedLanguage"}
                  className={props.classes.textField}
                  disabled={isVersionLocked}
                  onChange={(event) => {
                    applySelectedLanguageChange(event.target.value);
                  }}
                  value={selectedLanguage}
                >
                  {Object.entries(props.selectedBase.supportedLanguages).map(([languageKey, languageCaption]) => {
                    let color = props.selectedVersion?.createdLanguages?.includes(languageKey)
                      ? "#9bcec0"
                      : "#eeeeee";
                    return (
                      <MenuItem key={languageKey} value={languageKey} style={{ color: color }}>
                        {languageCaption}
                      </MenuItem>
                    );
                  })}
                </TextField>
                <Tooltip title="Remove selected language from this version">
                  <span>
                    <IconButton
                      size="small"
                      disabled={
                        isVersionLocked ||
                        selectedLanguage === "en" ||
                        !props.selectedVersion?.createdLanguages?.includes(selectedLanguage)
                      }
                      onClick={() => {
                        if (effectiveUserPermissions["editContent"] === false) {
                          props.createSnackbar(
                            "You do not have permission to update, contact your administrator.",
                            "warning",
                            false
                          );
                          return;
                        }
                        setRemoveLanguageDialog({ open: true, languageKey: selectedLanguage });
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </CardContent>
          </Card>
          <TextGameObjectEditorContainer
            selectedBase={props.selectedBase}
            selectedVersion={props.selectedVersion}
            selectedLanguage={selectedLanguage}
            classes={props.classes}
            setSelectedVersionPathValue={props.setSelectedVersionPathValue}
            userPermissions={effectiveUserPermissions}
          ></TextGameObjectEditorContainer>
          {_.has(props.selectedBase, "predefinedGameComponents") ? (
            <LocalizationContainerTreeView
              selectedModifiedBase={props.selectedModifiedBase}
              setSelectedModifiedBase={props.setSelectedModifiedBase}
              selectedBase={props.selectedBase}
              selectedVersion={props.selectedVersion}
              selectedGame={props.selectedGame}
              selectedLanguage={selectedLanguage}
              classes={props.classes}
              newSetPortrait={props.newSetPortrait}
              setSelectedVersionPathValue={props.setSelectedVersionPathValue}
              userPermissions={effectiveUserPermissions}
              createSnackbar={props.createSnackbar}
            ></LocalizationContainerTreeView>
          ) : (
            <LocalizationContainer
              selectedBase={props.selectedBase}
              selectedVersion={props.selectedVersion}
              selectedGame={props.selectedGame}
              selectedLanguage={selectedLanguage}
              newSetPortrait={props.newSetPortrait}
              classes={props.classes}
              setSelectedVersionPathValue={props.setSelectedVersionPathValue}
              userPermissions={effectiveUserPermissions}
              createSnackbar={props.createSnackbar}
            ></LocalizationContainer>
          )}
          <Card className={props.classes.card}>
            <CardContent className={props.classes.cardContent}>
              <Button
                id={"resetLocalizationSettings"}
                style={{ marginTop: "10px" }}
                disabled={isVersionLocked}
                onClick={() => {
                  if (effectiveUserPermissions["editContent"] === false) {
                    props.createSnackbar(
                      "You do not have permission to update, contact your administrator.",
                      "warning",
                      false
                    );
                    return;
                  }
                  resetLocalizationSettings();
                  if (props.defaultVersion?.versionID == props.selectedVersion._id) {
                    props.createSnackbar("There are no changes in the version", "warning");
                  }
                }}
              >
                RESET LOCALIZATION SETTINGS
              </Button>
            </CardContent>
          </Card>
        </>
      )}
      </>
    ),
    generalSettings: (
      <>
      {isSceneEditor ? (
        <SceneEditorGeneralSettings
          selectedVersion={props.selectedVersion}
          selectedBase={props.selectedBase}
          setSelectedVersionPathValue={props.setSelectedVersionPathValue}
          userPermissions={effectiveUserPermissions}
          createSnackbar={props.createSnackbar}
          isVersionLocked={isVersionLocked}
          userCompany={props.userCompany}
          versionRename={props.versionRename}
          setDefaultVersion={(value) => {
            props.setDefaultVersion(value);
          }}
          setDefaultVersionUpdated={(value) => {
            props.setDefaultVersionUpdated(value);
          }}
          defaultVersion={props.defaultVersion}
          getDefaultVersion={props.getDefaultVersion}
          defaultVersionUpdated={props.defaultVersionUpdated}
          searchBarValue={props.searchBarValue}
          setSearchBarValue={(value) => {
            props.setSearchBarValue(value);
          }}
          onResetGeneralSettings={() => {
            if (effectiveUserPermissions["editContent"] === false) {
              props.createSnackbar(
                "You do not have permission to update, contact your administrator.",
                "warning",
                false
              );
              return;
            }
            resetGeneralSettings();
            if (props.defaultVersion?.versionID == props.selectedVersion._id) {
              props.createSnackbar("There are no changes in the version", "warning");
            }
          }}
          onResetVersionSettings={() => {
            if (effectiveUserPermissions["editContent"] === false) {
              props.createSnackbar(
                "You do not have permission to update, contact your administrator.",
                "warning",
                false
              );
              return;
            }
            resetVersionSettings();
            if (props.defaultVersion?.versionID == props.selectedVersion._id) {
              props.createSnackbar("There are no changes in the version", "warning");
            }
          }}
          onResetLocalizationSettings={() => {
            if (effectiveUserPermissions["editContent"] === false) {
              props.createSnackbar(
                "You do not have permission to update, contact your administrator.",
                "warning",
                false
              );
              return;
            }
            resetLocalizationSettings();
            if (props.defaultVersion?.versionID == props.selectedVersion._id) {
              props.createSnackbar("There are no changes in the version", "warning");
            }
          }}
          onResetCategorization={() => {
            if (effectiveUserPermissions["editContent"] === false) {
              props.createSnackbar(
                "You do not have permission to update, contact your administrator.",
                "warning",
                false
              );
              return;
            }
            resetSceneEditorCategorization();
          }}
        />
      ) : (
        <>
          <GeneralSettingsEditor
            selectedVersion={props.selectedVersion}
            selectedBase={props.selectedBase}
            classes={props.classes}
            setSelectedVersionPathValue={props.setSelectedVersionPathValue}
            isPreviewLoaded={props.isPreviewLoaded}
            iFrameId={props.iFrameId}
            userPermissions={effectiveUserPermissions}
            createSnackbar={props.createSnackbar}
          ></GeneralSettingsEditor>
          <Card className={props.classes.card}>
            <CardContent className={props.classes.cardContent}>
              <Button
                style={{ marginTop: "10px" }}
                disabled={isVersionLocked}
                onClick={() => {
                  if (effectiveUserPermissions["editContent"] === false) {
                    props.createSnackbar(
                      "You do not have permission to update, contact your administrator.",
                      "warning",
                      false
                    );
                    return;
                  }
                  resetGeneralSettings();
                  if (props.defaultVersion?.versionID == props.selectedVersion._id) {
                    props.createSnackbar("There are no changes in the version", "warning");
                  }
                  ReactGA.event({
                    category: "Config",
                    action: "Reset General Settings",
                    label:
                      "game: " +
                      props.selectedGame.title +
                      ", base: " +
                      props.selectedBase.title +
                      ", version: " +
                      props.selectedVersion.title,
                    dimension1: localStorage.getItem("user.id")
                  });
                  ReactGA4.event({
                    category: "Config",
                    action: "Reset General Settings",
                    label:
                      "game: " +
                      props.selectedGame.title +
                      ", base: " +
                      props.selectedBase.title +
                      ", version: " +
                      props.selectedVersion.title,
                    dimension1: localStorage.getItem("user.id")
                  });
                }}
              >
                RESET GENERAL SETTINGS
              </Button>
            </CardContent>
          </Card>
        </>
      )}
      </>
    ),
    agent: (
      <>
        <ContainerGamesAgent
          selectedVersion={props.selectedVersion}
          isVersionLocked={isVersionLocked}
          baseId={props.selectedBase && props.selectedBase._id ? String(props.selectedBase._id) : null}
          versions={props.versions}
          createSnackbar={props.createSnackbar}
          patchVersionFields={props.patchVersionFields}
          fetchVersions={props.fetchVersions}
          isAdmin={props.isAdmin}
          userCompany={agentCompany}
          refreshCompany={refreshAgentCompany}
          doRefresh={props.doRefresh}
        />
      </>
    ),
  };

  const normalTabPanels = legacyTabDefs.map((tab, index) => (
    <TabPanel key={tab.key} value={tabValue} index={index} className={classes.tabPanel}>
      {normalPanelsByKey[tab.key]}
    </TabPanel>
  ));

  const tabContent = (
    <SwipeableViews
      style={{ flex: 1, overflow: "hidden" }}
      axis={theme.direction === "rtl" ? "x-reverse" : "x"}
      index={tabValue}
      animateTransitions={!isSceneEditor}
      onChangeIndex={(index) => {
        setTabValue(index);
      }}
      ref={swipeableViewRef}
    >
      {normalTabPanels}
    </SwipeableViews>
  );

  return (
    <div id={"gameConfigContainerDiv"} className={classes.gameConfigContainerDiv}>
      {isVersionLocked ? (
        <div
          style={{
            backgroundColor: "#3d3d3d",
            color: "#f5f5f5",
            padding: "8px 12px",
            fontSize: "12px",
            borderBottom: "1px solid rgba(81, 81, 81, 1)"
          }}
        >
          This version is locked. Unlock to edit content.
        </div>
      ) : null}

      {isSceneEditor ? (
        <div
          style={{
            display: "flex",
            flex: 1,
            minHeight: 0,
            backgroundColor: SCENE_EDITOR_PANEL_BG,
          }}
        >
          <div
            role="tablist"
            style={{
              display: "flex",
              flexDirection: "column",
              width: 44,
              flexShrink: 0,
              overflow: "hidden",
              borderRight: `1px solid ${SCENE_EDITOR_SECTION_DIVIDER}`,
            }}
          >
            {legacyTabDefs.map((tab) => {
              const selected = tabValue === tab.index;
              return (
                <Tooltip key={tab.key} title={tab.label} placement="right">
                  <IconButton
                    role="tab"
                    aria-selected={selected}
                    aria-label={tab.label}
                    id={`simple-tab-${tab.index}`}
                    aria-controls={`simple-tabpanel-${tab.index}`}
                    onClick={() => setTabValue(tab.index)}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 0,
                      color: selected ? theme.palette.primary.main : "#bbb",
                      opacity: selected ? 1 : 0.7,
                      borderLeft: selected
                        ? `2px solid ${theme.palette.primary.main}`
                        : "2px solid transparent",
                      backgroundColor: selected ? "rgba(144, 202, 249, 0.12)" : "transparent",
                    }}
                  >
                    {tab.icon}
                  </IconButton>
                </Tooltip>
              );
            })}
          </div>
          <div style={{ flex: 1, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {tabContent}
          </div>
        </div>
      ) : (
        <>
          <Tabs
            id={"tabsComponent"}
            indicatorColor={"primary"}
            style={{
              height: "60px",
              borderColor: "black",
              borderWidth: "1px",
              borderStyle: "none none solid none"
            }}
            value={tabValue}
            onChange={(event, newValue) => {
              setTabValue(newValue);
            }}
            variant={"fullWidth"}
          >
            {legacyTabDefs.map((tab) => (
              <Tab
                key={tab.key}
                wrapped
                style={{
                  height: "60px",
                  minHeight: "0px",
                  minWidth: "0px",
                  fontSize: "9px",
                  padding: "0px",
                  flex: "1"
                }}
                label={tab.label}
                icon={tab.icon}
                {...a11yProps(tab.index)}
              />
            ))}
          </Tabs>
          {tabContent}
        </>
      )}
    </div>
  );
}
